import * as React from "react"
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import _ from "lodash"
import { Appearance, Dimensions, Platform, TouchableOpacity, ViewStyle } from "react-native"
import { Box, Icon, useDisclose, View } from "native-base"
import { Input, InputProps } from "../input/input"
import { JalaIcon } from "../jala-icon/jala-icon"
import {
  PickerActionsheet,
  PickerActionsheetProps,
  PickerFlatListProps,
} from "../picker-actionsheet/picker-actionsheet"
import { Text } from "../text/text"
import { color, nativeBaseTheme } from "../../theme"
import { observer } from "mobx-react-lite"
import { TxKeyPath } from "../../i18n"
import { ListRenderItem, FlatListProps } from "../flat-list/flat-list"
import { useSafeAreaInsets } from "react-native-safe-area-context"

const CONTAINER: ViewStyle = {
  flex: 1,
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
}

const BOX_COLOR: ViewStyle = {
  height: 25,
  width: 25,
  borderRadius: 5,
  marginRight: 10
}

const ICON: ViewStyle = {
  marginRight: 10
}

export interface PickerInlineProps extends InputProps {
  data?: any[],
  fetchData?: (search?: string) => Promise<Array<any>>
  dataKey?: string,
  dataLabel?: string,
  titleTx?: TxKeyPath,

  isInvalid?: boolean,

  initializeSelectedDatum?: (callback: (item) => void) => void,
  onValueChange?: (value, index, item) => void,
  onOpen?: () => void,
  onClose?: () => void,

  pickerActionsheetProps?: Omit<PickerActionsheetProps, "renderItem"> & { renderItem?: ListRenderItem<any> | null | undefined },

  colorScheme?: "light" | "dark",
  actionSheetColorScheme?: "light" | "dark",
  clearable?: boolean,
  isUseSearch?: boolean,

  renderItemText?: ((item: any) => ReactElement) | ((item: any) => string) | undefined
  renderSelectedItemText?: ((item: any) => string) | undefined
  renderSelectedItem?: ((item: any) => ReactElement) | undefined

  value?: any
  isCheckModal?: boolean,

  buttonStyle?: ViewStyle
}

const ACTION_SHEET_ITEM: ViewStyle = {
  minHeight: 44,
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 16,
}

const defaultData = []
const defaultPickerActionsheetProps: Omit<PickerActionsheetProps, "renderItem"> = {}
const defaultFlatListProps: PickerFlatListProps<FlatListProps<any>> = {}

/**
 * Describe your component here
 */
export const PickerInline = observer(function Picker(props: PickerInlineProps) {
  const {
    colorScheme = Appearance.getColorScheme(),
    actionSheetColorScheme = colorScheme || Appearance.getColorScheme(), isUseSearch = true, clearable = false,
    value, data = defaultData, fetchData: fetchDataProp, dataKey = 'id', dataLabel = 'name', isInvalid,
    renderItemText = undefined, renderSelectedItem = undefined, renderSelectedItemText = undefined,
    initializeSelectedDatum, onValueChange, onOpen: onOpenProp, onClose: onCloseProp,
    pickerActionsheetProps: { flatListProps: { estimatedItemSize = 44, ...flatListProps } = defaultFlatListProps, inputProps, actionsheetStyle, ...restPickerActionsheetProps } = defaultPickerActionsheetProps,
    InputRightElement, isDisabled, isCheckModal, titleTx = "picker.choose", buttonStyle,
    ...rest
  } = props

  const [ models, setModels ] = useState<Array<any>>(data || [])
  const [ search, setSearch ] = useState<string>('')
  const [ selectedDatum, setSelectedDatum ] = useState<any>()
  const insets = useSafeAreaInsets()

  // const [groupValue, setGroupValue] = React.useState([]);
  const queryPromise = useRef<Promise<any>>(Promise.resolve())
  const previousSearch = useRef<string>(null)

  const { isOpen, onOpen, onClose: onCloseDisclose } = useDisclose()

  const fetchData = useCallback((search) => {
    return fetchDataProp && fetchDataProp(search) || Promise.resolve(data)
  }, [data, fetchDataProp])

  const queryModels = useMemo(() => _.debounce((search: string) => {
    queryPromise.current = queryPromise.current.then(() => {
      if (previousSearch.current === search) return undefined;
      previousSearch.current = search;
      return fetchData(search)
        .catch((error) => {
          console.warn(error)
          return []
        })
        .then((models) => {
          setModels(models)
        })
    })
  }), [fetchData])

  useEffect(() => {
    queryModels(search || '')
  }, [queryModels, search])

  useEffect(() => {
    setModels(data)
  }, [data])

  useEffect(() => {
    if (selectedDatum && selectedDatum[dataKey] === value) return;
    const selected = models.find((datum) => datum[dataKey] === value)
    if (selected) setSelectedDatum(selected)
    else if (initializeSelectedDatum) initializeSelectedDatum(setSelectedDatum)
    else setSelectedDatum(undefined)
  }, [dataKey, initializeSelectedDatum, models, selectedDatum, value])

  useEffect(() => {
    if (isOpen) onOpenProp && onOpenProp()
  }, [isOpen, onOpenProp])

  const inputBaseStyle = useMemo(() => (
    nativeBaseTheme.components.InputBase.baseStyle({ colorMode: colorScheme })
  ), [colorScheme]);

  const onClose: () => void = useCallback(() => {
    onCloseProp && onCloseProp()
    onCloseDisclose()
  }, [onCloseDisclose, onCloseProp])

  const onPressItem: (item) => any = useCallback((item) => () => {
    onValueChange(item && item[dataKey], models.findIndex((datum) => datum === item), item)
  }, [dataKey, models, onValueChange])

  const leftElementItem = useCallback(({ item }) => {
    return (item.icon
      ? <JalaIcon name={item.icon} color={color.primary} size={25} style={ICON} />
      : <Box backgroundColor={item.color} style={BOX_COLOR} />
    )
  }, [])

  const renderItem: ListRenderItem<any> = useCallback(({ item }) => {
    const isUseLeftElement = item.icon || item.color
    return (
      <View style={ACTION_SHEET_ITEM}>
        {isUseLeftElement && leftElementItem({ item })}
        {dataLabel === 'tx'
          ? <Text tx={item[dataLabel]} preset={item[dataKey] === value ? "title4" : "body2"} colorScheme={actionSheetColorScheme} isTruncated />
          : <Text preset={item[dataKey] === value ? "title4" : "body2"} colorScheme={actionSheetColorScheme} isTruncated >{(renderItemText && renderItemText(item)) || (renderSelectedItemText && renderSelectedItemText(item)) || item[dataLabel]}</Text>}
      </View>
    )
  }, [actionSheetColorScheme, dataKey, dataLabel, leftElementItem, renderItemText, renderSelectedItemText, value])

  const minHeightMax = Dimensions.get("window").height * (Platform.OS === 'web' ? 0.5 : 0.8);
  const minHeightMin = Dimensions.get("window").height * 0.3;
  const estimatedHeight = (estimatedItemSize * models.length)
    + 24 + 32
    + (props.isUseSearch && 40 || 0) + 7
    + (restPickerActionsheetProps?.onPressBottomButton && 60 || 0)
    + insets.bottom;
  let minHeight = minHeightMax;
  if (estimatedHeight < minHeightMin)
    minHeight = minHeightMin;
  else if (estimatedHeight < minHeightMax)
    minHeight = estimatedHeight;

  return (
    <View flexDirection={"row"} alignContent={"flex-start"} alignItems={"center"}>
      <TouchableOpacity
        onPress={onOpen} disabled={isDisabled}
        style={{...buttonStyle}}>
        {renderSelectedItem && renderSelectedItem(selectedDatum) ? (
          <View {...inputBaseStyle} { ...(isInvalid ? inputBaseStyle._invalid : {}) } p={0} style={CONTAINER}>
            {renderSelectedItem(selectedDatum)}
            {InputRightElement || <Icon opacity={isDisabled ? 50 : 100} as={<MaterialIcons name="keyboard-arrow-down" />} testID={`${(rest.testID || "")}-show-options`}
                   size={"24px"} mr={"9px"} textAlign="center" backgroundColor={rest.backgroundColor} color={color.palette.gray600}
                   onPress={!isDisabled && onOpen || undefined} />}
          </View>
        ) : <Input
          colorScheme={colorScheme}
          isReadOnly
          isDisabled={isDisabled}
          value={(renderSelectedItemText && renderSelectedItemText(selectedDatum)) || selectedDatum?.[dataLabel] || ''}
          pointerEvents="none"
          {...rest}
          InputRightElement={InputRightElement ||
            <Icon as={<MaterialIcons name="keyboard-arrow-down" />} testID={`${(rest.testID || "")}-show-options`}
                  size={"24px"} mr={"9px"} textAlign="center" backgroundColor={rest.backgroundColor} color="#666666"
                  onPress={!isDisabled && onOpen || undefined} />}
        />}
      </TouchableOpacity>
      <PickerActionsheet
        colorScheme={actionSheetColorScheme}
        isOpen={isOpen}
        onClose={onClose}
        data={models}
        // titleTx="picker.choose"
        titleTx={titleTx}
        searchDefaultValue={search}
        inputProps={{
          onChangeText: setSearch,
          ...inputProps
        }}
        onPressItem={onPressItem}
        renderItem={renderItem}
        withoutSearch={!isUseSearch}
        clearable={clearable}
        isCheckModal={isCheckModal}
        flatListProps={{
          keyExtractor: (item, index) => `${item[dataKey] || index}`,
          estimatedItemSize,
          ...flatListProps
        }}
        actionsheetStyle={{
          minHeight,
          ...(Platform.OS === 'web' ? { maxHeight: Dimensions.get("window").height * 0.5 } : {}),
          ...actionsheetStyle
        }}
        {...restPickerActionsheetProps}
      />
    </View>
  )
})
